import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertTriangle, CalendarClock, CheckCheck, CheckCircle2, History, Play, RotateCcw, Sparkles, XCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api, type DepartmentRef } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime, formatMinutes, formatNumber } from '../lib/format';
import {
  RUN_TONE,
  STATUSES,
  STATUS_TONE,
  codeText,
  formatWorkDate,
  type ApproveAllPreview,
  type ApproveAllResult,
  type EvaluationList,
  type EvaluationRow,
  type EvaluationStatus,
  type Overview,
  type RunKind,
  type RunSummary,
} from '../lib/ai-points';
import { AiPointsSettingsPanel } from '../components/AiPointsSettingsPanel';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  Modal,
  PageHeader,
  Pagination,
  Select,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
  useToast,
} from '../components/ui';

const BASE = '/ai-daily-points';

/** The run's own label: what made it, and whether it served as the first-install trial. */
export function RunBadges({ run }: { run: { runKind: RunKind; servedAsInitialTrial: boolean } }) {
  const { t } = useI18n();
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {run.servedAsInitialTrial && (
        <Badge tone="plasma">
          <Sparkles className="me-1 inline h-3 w-3" aria-hidden />
          {t('aiPoints.runKind.INITIAL_YESTERDAY_TRIAL')}
        </Badge>
      )}
      {(!run.servedAsInitialTrial || run.runKind !== 'INITIAL_YESTERDAY_TRIAL') && (
        <Badge tone="neutral">{t(`aiPoints.runKind.${run.runKind}` as TranslationKey)}</Badge>
      )}
    </span>
  );
}

export function StatusBadge({ status }: { status: EvaluationStatus }) {
  const { t } = useI18n();
  return <Badge tone={STATUS_TONE[status]}>{t(`aiPoints.status.${status}` as TranslationKey)}</Badge>;
}

/** The score as a big number out of 10 — proposed, and what was credited once decided. */
export function Score({ row }: { row: Pick<EvaluationRow, 'score' | 'creditedPoints' | 'status'> }) {
  const { t, locale } = useI18n();
  if (row.score == null) return <span className="text-muted">—</span>;
  return (
    <span className="inline-flex flex-col items-end leading-tight">
      <span className={clsx('text-h3 tabular', row.score > 0 ? 'text-primary-600 dark:text-primary-400' : 'text-[var(--text-secondary)]')}>
        <bdi dir="ltr">
          {formatNumber(row.score, locale)}
          <span className="text-caption text-faint">/10</span>
        </bdi>
      </span>
      {row.status === 'APPROVED' && (
        <span className="text-caption text-success-fg dark:text-success">
          {t('aiPoints.credited', { n: formatNumber(row.creditedPoints ?? 0, locale) })}
        </span>
      )}
      {row.status === 'REJECTED' && <span className="text-caption text-muted">{t('aiPoints.nothingCredited')}</span>}
    </span>
  );
}

/**
 * Route: `/ai-points` — AI Daily Points (CR-007), the administrators' (`ai_points.view_all` to read,
 * `ai_points.review` to decide). To review (a date, `?date=`), Daily history and Settings tabs (`?tab=`).
 * Every number and reason here is the administrators' alone: employees see only the credited points.
 */
export function AiPointsPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const canReview = can(P.AI_POINTS_REVIEW);
  const tabs = [
    { id: 'review', label: t('aiPoints.tabReview') },
    { id: 'history', label: t('aiPoints.tabHistory') },
    ...(canReview ? [{ id: 'settings', label: t('aiPoints.tabSettings') }] : []),
  ];
  const requested = params.get('tab');
  const tab = tabs.some((x) => x.id === requested) ? requested! : 'review';

  const overview = useQuery({
    queryKey: ['ai-points', 'overview'],
    queryFn: () => api.get<Overview>(`${BASE}/overview`),
    // A run being evaluated fills in as the model answers.
    refetchInterval: (q) => (q.state.data?.runs.some((r) => r.status === 'EVALUATING' || !r.preparedAt) ? 5_000 : 60_000),
  });

  const runNow = useMutation({
    mutationFn: () => api.post<{ runId: string; workDate: string }>(`${BASE}/runs`),
    onSuccess: (r) => {
      toast.success(t('aiPoints.runStarted', { date: r.workDate }));
      void queryClient.invalidateQueries({ queryKey: ['ai-points'] });
      setParams({ date: r.workDate }, { replace: true });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <>
      <PageHeader
        title={t('aiPoints.title')}
        subtitle={t('aiPoints.subtitle')}
        action={
          canReview && tab === 'review' ? (
            <Button icon={<Play className="h-4 w-4" />} loading={runNow.isPending} onClick={() => runNow.mutate()}>
              {t('aiPoints.runNow')}
            </Button>
          ) : null
        }
      />
      <Tabs active={tab} onChange={(id) => setParams(id === 'review' ? {} : { tab: id }, { replace: true })} tabs={tabs} />
      <div className="mt-5">
        {tab === 'review' &&
          (overview.isLoading ? (
            <LoadingBlock />
          ) : overview.error ? (
            <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
          ) : (
            <ReviewTab overview={overview.data!} />
          ))}
        {tab === 'history' && <HistoryTab />}
        {tab === 'settings' && canReview && <AiPointsSettingsPanel />}
      </div>
    </>
  );
}

/** The server's state, said once at the top: blocked without OpenAI, or switched off. */
function ServerNotice({ overview }: { overview: Overview }) {
  const { t, locale } = useI18n();
  if (!overview.server.ready) {
    return (
      <Card className="mb-4 border-error/40">
        <div className="flex items-start gap-3">
          <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-error-fg dark:text-error-dark" aria-hidden />
          <div className="min-w-0 space-y-1">
            <p className="font-medium text-[var(--text-primary)]">{t('aiPoints.blockedTitle')}</p>
            <p className="text-body-sm text-[var(--text-secondary)]">{t('aiPoints.blockedBody')}</p>
          </div>
        </div>
      </Card>
    );
  }
  if (!overview.server.enabled) {
    return (
      <Card className="mb-4">
        <p className="text-body-sm text-[var(--text-secondary)]">{t('aiPoints.disabledBody')}</p>
      </Card>
    );
  }
  return (
    <p className="mb-4 flex flex-wrap items-center gap-1.5 text-body-sm text-muted">
      <CalendarClock className="h-4 w-4" aria-hidden />
      {t('aiPoints.nextRun', {
        time: overview.server.scheduleTime,
        timezone: overview.server.timezone,
        at: overview.server.nextRunAt ? formatDateTime(overview.server.nextRunAt, locale) : '—',
      })}
      {overview.server.model && <span className="font-mono text-mono-sm text-faint">· {overview.server.model}</span>}
    </p>
  );
}

function ReviewTab({ overview }: { overview: Overview }) {
  const { t, locale } = useI18n();
  const [params, setParams] = useSearchParams();
  const runs = overview.runs;
  const date = params.get('date') ?? runs[0]?.workDate ?? null;
  const run = runs.find((r) => r.workDate === date) ?? null;

  return (
    <>
      <ServerNotice overview={overview} />
      {runs.length > 0 && (
        <nav className="mb-4 flex gap-2 overflow-x-auto pb-1" aria-label={t('aiPoints.recentRuns')}>
          {runs.map((r) => {
            const pending = r.counts.PENDING_ADMIN_APPROVAL ?? 0;
            return (
              <button
                key={r.id}
                onClick={() => setParams({ date: r.workDate }, { replace: true })}
                aria-current={r.workDate === date ? 'true' : undefined}
                className={clsx(
                  'surface shrink-0 rounded-md px-3 py-2 text-start transition-colors duration-fast',
                  r.workDate === date ? 'border-primary ring-1 ring-primary/40' : 'hover:border-primary/50',
                )}
              >
                <span className="block text-body-sm font-medium text-[var(--text-primary)]">{formatWorkDate(r.workDate, locale)}</span>
                <span className="mt-0.5 flex items-center gap-1.5 text-caption text-muted">
                  {r.servedAsInitialTrial && <Sparkles className="h-3 w-3 text-ai-plasma-text dark:text-ai-plasma-lift" aria-label={t('aiPoints.runKind.INITIAL_YESTERDAY_TRIAL')} />}
                  {pending > 0 ? t('aiPoints.pendingCount', { n: formatNumber(pending, locale) }) : t(`aiPoints.runStatus.${r.status}` as TranslationKey)}
                </span>
              </button>
            );
          })}
        </nav>
      )}
      {!date ? (
        <Card padded={false}>
          <EmptyState icon={<Sparkles className="h-8 w-8" />} title={t('aiPoints.noRuns')} hint={t('aiPoints.noRunsHint')} />
        </Card>
      ) : !run ? (
        <Card padded={false}>
          <EmptyState icon={<Sparkles className="h-8 w-8" />} title={t('aiPoints.noRunForDate', { date: formatWorkDate(date, locale) })} hint={t('aiPoints.noRunForDateHint')} />
        </Card>
      ) : (
        <>
          <RunCard run={run} canReview={overview.canReview} />
          <EvaluationTable workDate={run.workDate} />
        </>
      )}
    </>
  );
}

/** One run's head: what made it, when, how far it got, and what it cost in model calls. */
function RunCard({ run, canReview }: { run: RunSummary; canReview: boolean }) {
  const { t, locale } = useI18n();
  const [approvingAll, setApprovingAll] = useState(false);
  const c = run.counts;
  const n = (s: EvaluationStatus) => c[s] ?? 0;
  const evaluated = n('PENDING_ADMIN_APPROVAL') + n('APPROVED') + n('REJECTED');
  const retryable = n('DEFERRED') + n('FAILED_AI') + n('BLOCKED_CONFIGURATION');
  const stats: Array<[TranslationKey, number, string?]> = [
    ['aiPoints.stat.evaluated', evaluated],
    ['aiPoints.stat.pending', n('PENDING_ADMIN_APPROVAL'), 'text-warning-fg dark:text-warning-dark'],
    ['aiPoints.stat.approved', n('APPROVED'), 'text-success-fg dark:text-success'],
    ['aiPoints.stat.zeroApprovals', run.zeroApprovals],
    ['aiPoints.stat.rejected', n('REJECTED')],
    ['aiPoints.stat.deferred', n('DEFERRED')],
    ['aiPoints.stat.failed', n('FAILED_AI') + n('BLOCKED_CONFIGURATION'), 'text-error-fg dark:text-error-dark'],
    ['aiPoints.stat.notEligible', n('NOT_ELIGIBLE')],
    ['aiPoints.stat.inProgress', n('QUEUED') + n('EVALUATING')],
  ];
  return (
    <Card className="mb-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-h3 text-[var(--text-primary)]">{formatWorkDate(run.workDate, locale)}</h2>
            <RunBadges run={run} />
            <Badge tone={RUN_TONE[run.status]}>{t(`aiPoints.runStatus.${run.status}` as TranslationKey)}</Badge>
          </div>
          <p className="text-caption text-muted">
            {t('aiPoints.runTimes', {
              started: formatDateTime(run.startedAt, locale),
              cutoff: formatDateTime(run.dataCutoffAt, locale),
            })}
            {' · '}
            {t('aiPoints.runUsage', {
              calls: formatNumber(run.modelCalls, locale),
              tokensIn: formatNumber(run.inputTokens, locale),
              tokensOut: formatNumber(run.outputTokens, locale),
            })}
            {' · '}
            {t('aiPoints.runCredited', { n: formatNumber(run.creditedPoints, locale) })}
          </p>
          {run.lastError && <p className="text-caption text-error-fg dark:text-error-dark">{run.lastError}</p>}
        </div>
        {canReview && (
          <div className="flex flex-wrap gap-2">
            {n('PENDING_ADMIN_APPROVAL') > 0 && (
              <Button size="sm" variant="primary" icon={<CheckCheck className="h-4 w-4" />} onClick={() => setApprovingAll(true)}>
                {t('aiPoints.approveAll', { n: formatNumber(n('PENDING_ADMIN_APPROVAL'), locale) })}
              </Button>
            )}
            {retryable > 0 && (
              <Link to={`/ai-points/runs/${run.id}/retry`}>
                <Button size="sm" icon={<RotateCcw className="h-4 w-4" />}>
                  {t('aiPoints.retryRun', { n: formatNumber(retryable, locale) })}
                </Button>
              </Link>
            )}
            {n('PENDING_ADMIN_APPROVAL') > 0 && (
              <Link to={`/ai-points/runs/${run.id}/retry?pending=1`}>
                <Button size="sm" variant="ghost" icon={<RotateCcw className="h-4 w-4" />}>
                  {t('aiPoints.regenerateUnapproved')}
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>
      <dl className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-5 xl:grid-cols-9">
        {stats.map(([label, value, tone]) => (
          <div key={label} className="flex min-w-0 flex-col justify-between">
            <dt className="text-caption uppercase leading-tight tracking-wide text-[var(--text-tertiary)] [overflow-wrap:anywhere]">{t(label)}</dt>
            <dd className={clsx('text-h3 tabular', tone ?? 'text-[var(--text-primary)]')}>{formatNumber(value, locale)}</dd>
          </div>
        ))}
      </dl>
      {approvingAll && <ApproveAllDialog run={run} onClose={() => setApprovingAll(false)} />}
    </Card>
  );
}

/**
 * Approve all (operator, 2026-11-04: "add approve all") — a question with nothing typed, so a dialog: what
 * would be approved, read fresh from the server (how many, the points in all and who gets them, how many
 * are low-confidence, what is left out and why), then every listed proposal approved on its own, with
 * the revision shown. "Only the confident" leaves the low-confidence ones for a closer look.
 */
function ApproveAllDialog({ run, onClose }: { run: RunSummary; onClose: () => void }) {
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const preview = useQuery({
    queryKey: ['ai-points', 'approve-all', run.id],
    queryFn: () => api.get<ApproveAllPreview>(`${BASE}/runs/${run.id}/approve-all`),
    staleTime: 0,
  });
  const approve = useMutation({
    mutationFn: (onlyConfident: boolean) =>
      api.post<ApproveAllResult>(`${BASE}/runs/${run.id}/approve-all`, {
        items: (preview.data?.items ?? []).filter((i) => !onlyConfident || !i.needsConfirmation).map((i) => ({ id: i.id, revision: i.revision })),
        confirmLowConfidence: !onlyConfident,
      }),
    onSuccess: (r) => {
      const figures = { n: formatNumber(r.approved + r.replayed, locale), points: formatNumber(r.creditedPoints, locale), failed: formatNumber(r.failed.length, locale) };
      if (r.failed.length) toast.toast(t('aiPoints.approveAllPartial', figures), 'info');
      else toast.success(t('aiPoints.approveAllDone', figures));
      void queryClient.invalidateQueries({ queryKey: ['ai-points'] });
      onClose();
    },
    onError: (e: Error) => {
      toast.error(e.message);
      void queryClient.invalidateQueries({ queryKey: ['ai-points'] });
    },
  });
  const p = preview.data;
  const confident = p ? p.count - p.lowConfidence : 0;
  const withPoints = p ? p.items.filter((i) => i.score > 0) : [];
  const left = p
    ? [
        p.skipped.SELF ? t('aiPoints.approveAllLeftSelf', { n: formatNumber(p.skipped.SELF, locale) }) : null,
        p.skipped.ADMINISTRATOR ? t('aiPoints.approveAllLeftAdmin', { n: formatNumber(p.skipped.ADMINISTRATOR, locale) }) : null,
        p.skipped.INACTIVE ? t('aiPoints.approveAllLeftInactive', { n: formatNumber(p.skipped.INACTIVE, locale) }) : null,
      ].filter(Boolean)
    : [];
  const busy = approve.isPending;
  return (
    <Modal
      open
      role="alertdialog"
      size="md"
      onClose={() => !busy && onClose()}
      title={t('aiPoints.approveAllTitle', { date: formatWorkDate(run.workDate, locale) })}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          {p && p.lowConfidence > 0 && confident > 0 && (
            <Button onClick={() => approve.mutate(true)} disabled={busy}>
              {t('aiPoints.approveConfidentOnly', { n: formatNumber(confident, locale) })}
            </Button>
          )}
          <Button variant="primary" loading={busy} disabled={!p || p.count === 0} onClick={() => approve.mutate(false)} autoFocus>
            {t('aiPoints.approveAllConfirm', { n: formatNumber(p?.count ?? 0, locale) })}
          </Button>
        </>
      }
    >
      {preview.isLoading ? (
        <LoadingBlock />
      ) : preview.error ? (
        <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />
      ) : !p || p.count === 0 ? (
        <p className="text-body text-[var(--text-secondary)]">{t('aiPoints.approveAllNothing')}</p>
      ) : (
        <div className="space-y-3 text-body-sm text-[var(--text-secondary)]">
          <p className="text-body text-[var(--text-primary)]">
            {t('aiPoints.approveAllFigures', {
              n: formatNumber(p.count, locale),
              points: formatNumber(p.points, locale),
              people: formatNumber(p.withPoints, locale),
              zero: formatNumber(p.count - p.withPoints, locale),
            })}
          </p>
          {withPoints.length > 0 && (
            <div>
              <p className="mb-1 text-caption uppercase tracking-wide text-[var(--text-tertiary)]">{t('aiPoints.approveAllWithPoints')}</p>
              <ul className="flex flex-wrap gap-1.5">
                {withPoints.slice(0, 12).map((i) => (
                  <li key={i.id} className="rounded-xs bg-[var(--surface-sunken)] px-2 py-0.5">
                    {localized(i.employee)}{' '}
                    <bdi dir="ltr" className="tabular text-success-fg dark:text-success">
                      +{formatNumber(i.score, locale)}
                    </bdi>
                    {i.needsConfirmation && <AlertTriangle className="ms-1 inline h-3 w-3 text-warning-fg dark:text-warning-dark" aria-label={t('aiPoints.uncertainty.HIGH')} />}
                  </li>
                ))}
                {withPoints.length > 12 && <li className="px-2 py-0.5 text-muted">{t('aiPoints.approveAllMore', { n: formatNumber(withPoints.length - 12, locale) })}</li>}
              </ul>
            </div>
          )}
          {p.lowConfidence > 0 && (
            <p className="flex items-start gap-2 rounded-md bg-warning-bg p-2 text-warning-fg dark:bg-warning/14 dark:text-warning-dark">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              {t('aiPoints.approveAllLow', { n: formatNumber(p.lowConfidence, locale) })}
            </p>
          )}
          {left.length > 0 && <p className="text-muted">{t('aiPoints.approveAllLeft', { list: left.join(' · ') })}</p>}
          <p>{t('aiPoints.approveAllEach')}</p>
        </div>
      )}
    </Modal>
  );
}

function useDepartments() {
  return useQuery({ queryKey: ['departments', 'options'], queryFn: () => api.get<DepartmentRef[]>('/departments/options') });
}

/** To review: every employee's day for one work date, pending first, each approved or rejected on its own. */
function EvaluationTable({ workDate }: { workDate: string }) {
  const { t, localized } = useI18n();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<EvaluationStatus | ''>('');
  const [departmentId, setDepartmentId] = useState('');
  const [q, setQ] = useState('');
  const [minScore, setMinScore] = useState('');
  const departments = useDepartments();
  useEffect(() => setPage(1), [workDate, status, departmentId, q, minScore]);

  const list = useQuery({
    queryKey: ['ai-points', 'list', workDate, status, departmentId, q, minScore, page],
    queryFn: () =>
      api.get<EvaluationList>(`${BASE}/evaluations`, {
        workDate,
        page,
        pageSize: 50,
        ...(status ? { status } : {}),
        ...(departmentId ? { departmentId } : {}),
        ...(q.trim() ? { q: q.trim() } : {}),
        ...(minScore ? { minScore } : {}),
      }),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => (query.state.data?.items.some((r) => r.status === 'QUEUED' || r.status === 'EVALUATING') ? 5_000 : false),
  });

  return (
    <>
      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('aiPoints.searchEmployee')} aria-label={t('common.search')} />
          <Select value={status} onChange={(e) => setStatus(e.target.value as EvaluationStatus | '')} aria-label={t('common.status')}>
            <option value="">
              {t('common.status')} — {t('common.all')}
            </option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`aiPoints.status.${s}` as TranslationKey)}
                {list.data?.counts[s] ? ` (${list.data.counts[s]})` : ''}
              </option>
            ))}
          </Select>
          <Select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} aria-label={t('common.department')}>
            <option value="">
              {t('common.department')} — {t('common.all')}
            </option>
            {(departments.data ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {localized(d)}
              </option>
            ))}
          </Select>
          <Select value={minScore} onChange={(e) => setMinScore(e.target.value)} aria-label={t('aiPoints.minScore')}>
            <option value="">{t('aiPoints.anyScore')}</option>
            {[1, 3, 5, 7].map((s) => (
              <option key={s} value={s}>
                {t('aiPoints.scoreAtLeast', { n: s })}
              </option>
            ))}
          </Select>
        </div>
      </Card>
      <Card padded={false}>
        {list.isLoading ? (
          <LoadingBlock />
        ) : list.error ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data?.items.length ? (
          <EmptyState icon={<CheckCircle2 className="h-8 w-8" />} title={t('aiPoints.noRows')} hint={t('aiPoints.noRowsHint')} />
        ) : (
          <>
            <ReviewRows rows={list.data.items} />
            <Pagination page={list.data.page} pageCount={list.data.pageCount} total={list.data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}

/** The table of one date's rows, with the decisions. */
function ReviewRows({ rows }: { rows: EvaluationRow[] }) {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const [approving, setApproving] = useState<EvaluationRow | null>(null);
  return (
    <>
      <Table>
        <thead>
          <tr>
            <Th>{t('common.employee')}</Th>
            <Th>{t('aiPoints.col.hours')}</Th>
            <Th>{t('aiPoints.col.free')}</Th>
            <Th>{t('aiPoints.col.work')}</Th>
            <Th>{t('aiPoints.col.collab')}</Th>
            <Th align="end">{t('aiPoints.col.score')}</Th>
            <Th>{t('common.status')}</Th>
            <Th align="end">{t('common.actions')}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const f = r.figures;
            return (
              <Tr key={r.id}>
                <Td>
                  <Link to={`/ai-points/${r.id}`} className="block font-medium text-[var(--text-primary)] hover:text-primary-600">
                    {localized(r.employee)}
                  </Link>
                  <span className="block text-caption text-muted">
                    {r.employee.employeeCode && <span className="font-mono text-mono-sm text-faint">{r.employee.employeeCode} · </span>}
                    {r.employee.departments.map((d) => localized(d)).join('، ') || '—'}
                  </span>
                </Td>
                <Td className="whitespace-nowrap text-body-sm">
                  {f && f.dayKind !== 'NO_SHIFT' && f.workedMinutes != null ? (
                    <>
                      <span className="block tabular">
                        {formatMinutes(f.workedMinutes, locale)}
                        <span className="text-faint"> / {f.standardMinutes != null ? formatMinutes(f.standardMinutes, locale) : t('aiPoints.notOwed')}</span>
                      </span>
                      {f.surplusMinutes > 0 && (
                        <span className="text-caption text-success-fg dark:text-success">+{formatMinutes(f.surplusMinutes, locale)}</span>
                      )}
                    </>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </Td>
                <Td className="whitespace-nowrap text-body-sm tabular">
                  {f && f.freeSessions > 0 ? (
                    <>
                      {formatMinutes(f.freeMinutes, locale)}
                      <span className="block text-caption text-muted">{t('aiPoints.sessions', { n: formatNumber(f.freeSessions, locale) })}</span>
                    </>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </Td>
                <Td className="whitespace-nowrap text-body-sm">
                  {f ? (
                    <>
                      <span className="block tabular">
                        {t('aiPoints.tasksShort', { n: formatNumber(f.tasksFinished, locale), v: formatNumber(f.tasksVerified, locale) })}
                      </span>
                      <span className="text-caption text-muted">
                        {t('aiPoints.opsShort', { k: formatNumber(f.tickets, locale), q: formatNumber(f.qTasks, locale) })}
                      </span>
                    </>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </Td>
                <Td className="whitespace-nowrap text-body-sm">
                  {f ? (
                    <span className="tabular">
                      {t('aiPoints.collabShort', {
                        c: formatNumber(f.meaningfulCalls, locale),
                        r: formatNumber(f.reviews, locale),
                        m: formatNumber(f.comments, locale),
                      })}
                    </span>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </Td>
                <Td align="end">
                  <Score row={r} />
                </Td>
                <Td>
                  <StatusBadge status={r.status} />
                  {r.statusReason && <span className="mt-0.5 block text-caption text-muted">{codeText(t, 'aiPoints.reason', r.statusReason)}</span>}
                  {r.uncertainty && r.status === 'PENDING_ADMIN_APPROVAL' && (
                    <span className={clsx('mt-0.5 flex items-center gap-1 text-caption', r.uncertainty === 'HIGH' ? 'text-warning-fg dark:text-warning-dark' : 'text-muted')}>
                      {r.uncertainty === 'HIGH' && <AlertTriangle className="h-3 w-3" aria-hidden />}
                      {t(`aiPoints.uncertainty.${r.uncertainty}` as TranslationKey)}
                    </span>
                  )}
                  {r.selfNotice && <span className="mt-0.5 block text-caption text-muted">{t(`aiPoints.self.${r.selfNotice}` as TranslationKey)}</span>}
                </Td>
                <Td align="end" className="whitespace-nowrap">
                  <div className="flex justify-end gap-1.5">
                    {r.can.approve && (
                      <Button size="sm" variant="primary" onClick={() => setApproving(r)}>
                        {t('aiPoints.approve')}
                      </Button>
                    )}
                    {r.can.reject && (
                      <Button size="sm" onClick={() => navigate(`/ai-points/${r.id}/reject`)}>
                        {t('aiPoints.reject')}
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/ai-points/${r.id}`)}>
                      {t('common.view')}
                    </Button>
                  </div>
                </Td>
              </Tr>
            );
          })}
        </tbody>
      </Table>
      {approving && <ApproveDialog row={approving} onClose={() => setApproving(null)} />}
    </>
  );
}

/**
 * Approve's yes-or-no (a question with nothing typed — the sanctioned `ConfirmDialog`): how many points
 * the wallet gets, a warning first when the proposal's confidence is low, and that 0 credits nothing.
 */
export function ApproveDialog({
  row,
  onClose,
  onDone,
}: {
  row: Pick<EvaluationRow, 'id' | 'score' | 'revision' | 'needsConfirmation' | 'employee' | 'workDate'>;
  onClose: () => void;
  onDone?: () => void;
}) {
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const approve = useMutation({
    mutationFn: () =>
      api.post<{ creditedPoints: number | null; replayed: boolean }>(`${BASE}/evaluations/${row.id}/approve`, {
        revision: row.revision,
        ...(row.needsConfirmation ? { confirmLowConfidence: true } : {}),
      }),
    onSuccess: (r) => {
      toast.success(
        r.replayed
          ? t('aiPoints.alreadyApproved')
          : r.creditedPoints
            ? t('aiPoints.approvedToast', { n: formatNumber(r.creditedPoints, locale), name: localized(row.employee) })
            : t('aiPoints.approvedZeroToast', { name: localized(row.employee) }),
      );
      void queryClient.invalidateQueries({ queryKey: ['ai-points'] });
      onClose();
      onDone?.();
    },
    onError: (e: Error) => {
      toast.error(e instanceof ApiError && e.code === 'AI_POINTS_PROPOSAL_CHANGED' ? t('aiPoints.changed') : e.message);
      void queryClient.invalidateQueries({ queryKey: ['ai-points'] });
      onClose();
    },
  });
  const points = row.score ?? 0;
  return (
    <ConfirmDialog
      title={t('aiPoints.approveTitle')}
      confirmLabel={points > 0 ? t('aiPoints.approveCredit', { n: formatNumber(points, locale) }) : t('aiPoints.approveZero')}
      busy={approve.isPending}
      onConfirm={() => approve.mutate()}
      onCancel={onClose}
    >
      {row.needsConfirmation && (
        <p className="flex items-start gap-2 rounded-md bg-warning-bg p-2 text-body-sm text-warning-fg dark:bg-warning/14 dark:text-warning-dark">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          {t('aiPoints.lowConfidenceWarning')}
        </p>
      )}
      <p>
        {points > 0
          ? t('aiPoints.approveBody', { n: formatNumber(points, locale), name: localized(row.employee), date: formatWorkDate(row.workDate, locale) })
          : t('aiPoints.approveZeroBody', { name: localized(row.employee), date: formatWorkDate(row.workDate, locale) })}
      </p>
      {points > 0 && <p className="text-body-sm text-muted">{t('aiPoints.approveNotice')}</p>}
    </ConfirmDialog>
  );
}

/** Daily history: every date, every employee, every decision — searchable, long-term. */
function HistoryTab() {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const twoWeeksAgo = useMemo(() => new Date(Date.now() - 14 * 86_400_000).toISOString().slice(0, 10), []);
  const [from, setFrom] = useState(twoWeeksAgo);
  const [to, setTo] = useState(today);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<EvaluationStatus | ''>('');
  const [runKind, setRunKind] = useState<RunKind | ''>('');
  const [departmentId, setDepartmentId] = useState('');
  const [page, setPage] = useState(1);
  const departments = useDepartments();
  useEffect(() => setPage(1), [from, to, q, status, runKind, departmentId]);

  const list = useQuery({
    queryKey: ['ai-points', 'history', from, to, q, status, runKind, departmentId, page],
    queryFn: () =>
      api.get<EvaluationList>(`${BASE}/evaluations`, {
        page,
        pageSize: 50,
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(q.trim() ? { q: q.trim() } : {}),
        ...(status ? { status } : {}),
        ...(runKind ? { runKind } : {}),
        ...(departmentId ? { departmentId } : {}),
      }),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-6">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label={t('common.from')} />
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label={t('common.to')} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('aiPoints.searchEmployee')} aria-label={t('common.search')} />
          <Select value={status} onChange={(e) => setStatus(e.target.value as EvaluationStatus | '')} aria-label={t('common.status')}>
            <option value="">
              {t('common.status')} — {t('common.all')}
            </option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`aiPoints.status.${s}` as TranslationKey)}
                {list.data?.counts[s] ? ` (${list.data.counts[s]})` : ''}
              </option>
            ))}
          </Select>
          <Select value={runKind} onChange={(e) => setRunKind(e.target.value as RunKind | '')} aria-label={t('aiPoints.runKindLabel')}>
            <option value="">
              {t('aiPoints.runKindLabel')} — {t('common.all')}
            </option>
            {(['SCHEDULED', 'INITIAL_YESTERDAY_TRIAL', 'MANUAL'] as const).map((k) => (
              <option key={k} value={k}>
                {t(`aiPoints.runKind.${k}` as TranslationKey)}
              </option>
            ))}
          </Select>
          <Select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} aria-label={t('common.department')}>
            <option value="">
              {t('common.department')} — {t('common.all')}
            </option>
            {(departments.data ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {localized(d)}
              </option>
            ))}
          </Select>
        </div>
      </Card>
      <Card padded={false}>
        {list.isLoading ? (
          <LoadingBlock />
        ) : list.error ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data?.items.length ? (
          <EmptyState icon={<History className="h-8 w-8" />} title={t('aiPoints.noHistory')} hint={t('aiPoints.noHistoryHint')} />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('aiPoints.col.date')}</Th>
                  <Th>{t('common.employee')}</Th>
                  <Th align="end">{t('aiPoints.col.score')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th>{t('aiPoints.runKindLabel')}</Th>
                  <Th>{t('aiPoints.col.decision')}</Th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((r) => (
                  <Tr key={r.id} onClick={() => navigate(`/ai-points/${r.id}`)}>
                    <Td className="whitespace-nowrap">{formatWorkDate(r.workDate, locale)}</Td>
                    <Td>
                      <span className="block">{localized(r.employee)}</span>
                      {r.employee.employeeCode && <span className="font-mono text-mono-sm text-faint">{r.employee.employeeCode}</span>}
                    </Td>
                    <Td align="end">
                      <Score row={r} />
                    </Td>
                    <Td>
                      <StatusBadge status={r.status} />
                      {r.statusReason && <span className="mt-0.5 block text-caption text-muted">{codeText(t, 'aiPoints.reason', r.statusReason)}</span>}
                    </Td>
                    <Td>
                      <RunBadges run={r.run} />
                    </Td>
                    <Td className="whitespace-nowrap text-body-sm">
                      {r.reviewedAt ? (
                        <>
                          <span className="block">{r.reviewedBy ? localized(r.reviewedBy) : '—'}</span>
                          <span className="text-caption tabular text-muted">{formatDateTime(r.reviewedAt, locale)}</span>
                        </>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={list.data.page} pageCount={list.data.pageCount} total={list.data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}
